import React, { useState } from "react";
import { IoIosPersonAdd } from "react-icons/io";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "../context/Auth";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

const ChangePassword = () => {
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const passwordRules = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const changePassword = async () => {
    try {
      setLoading(true);

      if (!password || !confirmPassword) {
        toast.error("Please fill in both fields.");
        return;
      }

      if (password !== confirmPassword) {
        toast.error("Passwords do not match. Please try again.");
        return;
      }

      if (
        !passwordRules.minLength ||
        !passwordRules.hasUpperCase ||
        !passwordRules.hasLowerCase ||
        !passwordRules.hasNumber ||
        !passwordRules.hasSpecialChar
      ) {
        toast.error("Password does not meet all the requirements.");
        return;
      }

      const body = JSON.stringify({
        new_password: password,
      });

      await requestWithAuth(`/users/change-password/`, "PUT", body, {
        "Content-Type": "application/json",
        
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Password changed successfully",
      });

      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      if(error.response.status === 400){
        toast.error(error.response.data)
      }else{
          toast.error("Failed to change password. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="py-[32px] flex items-center gap-3">
        <IoIosPersonAdd className="text-[19px]" />
        <h4 className="text-[19px]">
          <b>Profile</b> - Change Password
        </h4>
      </div>

      <div className="lg:w-[40%] mx-auto">
        <div className="mb-[20px] border border-[#ddd] rounded-[3px] bg-white">
          <div className="p-[20px]">
            <h5 className="my-[2px] text-[17px]">Change Password</h5>
          </div>
          <div className="p-[20px] pt-0">
            <div className="mb-[20px]">
              <label htmlFor="" className="font-[600]">
                New Password:
              </label>
              <input
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                type={showPassword ? "text" : "password"}
                className="border border-[#ddd] rounded-[3px] h-[36px] w-full text-[#333] text-[13px] px-[12px] py-[7px]"
              />

              {/* Live Password Rules */}
              <ul className="text-[13px] mt-2 pl-[5px]">
                <li
                  className={`flex items-center gap-2 ${
                    passwordRules.minLength ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {passwordRules.minLength ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least 8 characters
                </li>
                <li
                  className={`flex items-center gap-2 ${
                    passwordRules.hasUpperCase ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {passwordRules.hasUpperCase ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one uppercase letter (A-Z)
                </li>
                <li
                  className={`flex items-center gap-2 ${
                    passwordRules.hasLowerCase ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {passwordRules.hasLowerCase ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one lowercase letter (a-z)
                </li>
                <li
                  className={`flex items-center gap-2 ${
                    passwordRules.hasNumber ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {passwordRules.hasNumber ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one number (0-9)
                </li>
                <li
                  className={`flex items-center gap-2 ${
                    passwordRules.hasSpecialChar ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {passwordRules.hasSpecialChar ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one special character (!@#$%^&*)
                </li>
              </ul>
            </div>

            <div className="mb-[20px]">
              <label htmlFor="" className="font-[600]">
                Confirm Password:
              </label>
              <input
                onChange={(e) => setConfirmPassword(e.target.value)}
                value={confirmPassword}
                type={showPassword ? "text" : "password"}
                className="border border-[#ddd] rounded-[3px] h-[36px] w-full text-[#333] text-[13px] px-[12px] py-[7px]"
              />
            </div>

            <div className="flex items-center gap-1 mb-5">
              <input
                type="checkbox"
                id="showPass"
                onChange={() => setShowPassword(!showPassword)}
              />
              <label htmlFor="showPass" className="text-[13px]">
                Show password
              </label>
            </div>

            <div className="flex justify-end">
              {loading ? (
                <div className="bg-[#2195f3d9] flex items-center gap-2 px-[12px] py-[7px] text-[13px] rounded-[3px] mt-5 text-white">
                  <RotatingLines
                    visible={true}
                    height="16"
                    width="16"
                    color="black"
                    strokeColor="white"
                    strokeWidth="5"
                    animationDuration="0.75"
                    ariaLabel="rotating-lines-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                  Submitting
                </div>
              ) : (
                <button
                  onClick={changePassword}
                  className="bg-[#2196F3] px-[12px] py-[7px] text-[13px] rounded-[3px] mt-5 text-white"
                >
                  Change Password
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
